import type { Event, EventHours, EventHoursOccurrence, Maybe } from "../types";
import { EventType } from "../types";
import { reify, onDay } from "./hours";
import { isFuture, type TimeZone } from "../support/dates";

/**
 * Flattens all occurrences for an event into a list of occurrences
 * including the event and place.
 */
export function getHours(
  event: Event,
  from: Date,
  repeat: number,
  timeZone: string
): Array<EventHoursOccurrence> {
  return event.schedule.flatMap((hours) => {
    return reify<EventHoursOccurrence>(hours, from, repeat, timeZone);
  });
}

/**
 * Returns a flattened list of occurrences for a given week
 */
export function getWeeklySchedule(
  event: Event,
  startDate: Date,
  timeZone: string
): Array<EventHoursOccurrence> {
  return getHours(event, startDate, 6, timeZone);
}

/**
 * Returns all event occurrences for the date of the given time
 */
export function showingToday(
  occurrence: Array<EventHoursOccurrence>,
  date: Date,
  timeZone: string
): Array<EventHoursOccurrence> {
  return onDay(occurrence, date, timeZone);
}

/**
 * Returns all remaining showings of an event on the day of the given date
 */
export function remainingShowings(
  occurrences: Array<EventHoursOccurrence>,
  date: Date,
  timeZone: string
): Array<EventHoursOccurrence> {
  const occurrencesToday = showingToday(occurrences, date, timeZone);

  return occurrencesToday.filter((occurrence) => {
    return occurrence.until >= date;
  });
}

/**
 * Returns next showing for an event on the given date
 */
export function getNextOpening(
  hours: Array<EventHoursOccurrence>,
  date: Date,
  timeZone: string
): EventHoursOccurrence {
  return remainingShowings(hours, date, timeZone)[0];
}

type EventTime = Event & EventHoursOccurrence;

function mapEntry(events: Event[], occurrence: EventHoursOccurrence): Maybe<EventTime> {
  const event = events.find((e) => e.id == occurrence.eventId);

  if (event == null) {
    return null;
  }

  return {
    ...occurrence,
    ...event
  };
}

export function eventsByType<T extends Event>(events: T[], type: EventType): T[] {
  return events.filter((e) => e.type == type);
}

/**
 * Returns events for the given date and time zone, decorated with ocurrence data.
 * So it'll have event.title and also event.from/until. See `EventTime` above;
 */
export function getDailyEventTimes(
  events: Event[],
  eventHours: EventHours[],
  time: Date,
  timeZone: TimeZone
) {
  const occurrences = eventHours.flatMap(function (hours: EventHours) {
    return reify<EventHoursOccurrence>(hours, time, 0, timeZone);
  }) as EventHoursOccurrence[];

  return occurrences.map(mapEntry.bind(null, events)).filter((e) => e != null);
}

export function futureEventTimes(times: EventTime[], time: Date, eventType: EventType) {
  return eventsByType(
    times.filter((e) => isFuture(e.from, time)),
    eventType
  );
}

export function getParkHours(dailyEventTimes: EventTime[]): Maybe<EventTime> {
  const parkOpen = eventsByType(dailyEventTimes, EventType.Open)[0];
  const parkClose = eventsByType(dailyEventTimes, EventType.Close)[0];

  if (parkOpen == null || parkClose == null) {
    return null;
  }

  return {
    ...parkOpen,
    title: "Park Hours",
    from: parkOpen.from,
    until: parkClose.from
  };
}
